import { create } from 'zustand';
import type {
  Tab,
  QueryTab,
  QueryTabContent,
  WorkspaceType,
  TabConnectionInfo,
  TabSourcePin,
  NewTabInput,
  SchemaCompareEndpoint,
} from '../types/workspaceTypes';
import { resolveEngineForConnectionId } from '../../schema-compare/utils/engineUtils';
import { isQueryTab, isNotebookTab, isPlSqlDebuggerTab, isPlSqlEditorTab, createDefaultQueryContent, createDefaultSecurityManagerContent, createDefaultSchemaCompareContent, createDefaultDataCompareContent, createDefaultDependencyViewerContent, createDefaultSchemaOverviewContent, createDefaultSpatialExplorerContent, createDefaultDataProfileContent, createDefaultStartPageContent, createDefaultNotebookContent, createDefaultSessionBrowserContent, createDefaultTablespaceManagerContent, createDefaultOracleObjectDetailContent, createDefaultDataPumpContent, createDefaultInstanceMonitorContent, createDefaultFilegroupManagerContent, createDefaultPlSqlDebuggerContent, createDefaultJobSchedulerContent, createDefaultPlSqlEditorContent, createDefaultReferencesContent, createDefaultDatabaseBlueprintContent, createDefaultCrossEngineMigrateContent, createDefaultDashboardContent, createDefaultDataImportContent, createDefaultDeadlockAnalyzerContent, createDefaultQueryStoreContent, createDefaultStorageExplorerContent } from '../types/workspaceTypes';
import { createDefaultPackageExplorerContent } from '../../package-explorer/types';
import { cleanupOrphanedPrefixes } from '../../query-editor/utils/queryNameGenerator';
import { useTabHistoryStore } from '../../../tab-history/store/tabHistoryStore';
import { useAppStore } from '../../../../store/appStore';
import { addBreadcrumb } from '../../../crash-dialog/services/breadcrumbService';
import { showRetentionToast } from '../../../script-browser/services/retentionToast';
import { releaseDeadlockAnalyzerTab } from '../../deadlock-analyzer/store/deadlockAnalyzerStore';
import { releaseStorageExplorerTab } from '../../storage-explorer/store/storageExplorerStore';
import { pageviewForTab } from './pageviewForTab';
import { safeTab, safeTabConnectionInfo, safeTabTitle } from './safeTabText';

// Re-export types for backward compatibility
export type { Tab, QueryTab, QueryTabContent, WorkspaceType, TabConnectionInfo, TabSourcePin, NewTabInput };
export { isQueryTab, createDefaultQueryContent };

// Re-export QueryMessage for backward compatibility
export type { QueryMessage } from '../types/workspaceTypes';

const DEFAULT_START_PAGE_TAB_ID = 'tab-start';

function createStartPageTab(tabId: string = DEFAULT_START_PAGE_TAB_ID): Tab {
  return {
    id: tabId,
    title: 'Home',
    type: 'start-page',
    content: createDefaultStartPageContent(),
  } as Tab;
}

/**
 * Azure Storage S1 — every path that drops a storage-explorer tab cancels its
 * in-flight listings and releases its main-process cursors (idempotent).
 */
function cleanupStorageExplorerTabs(tabs: Tab[]) {
  for (const tab of tabs) {
    if (tab.type === 'storage-explorer') releaseStorageExplorerTab(tab.id);
  }
}

/** Fire-and-forget cleanup of backend debug sessions for tabs being removed. */
function cleanupDebuggerTabs(tabs: Tab[]) {
  for (const tab of tabs) {
    if ((isPlSqlDebuggerTab(tab) || isPlSqlEditorTab(tab)) && tab.content.sessionKey) {
      const { sessionKey, debugState } = tab.content;
      void (async () => {
        try {
          if (debugState === 'stopped') {
            // Program already finished — just detach
            await window.electronAPI?.oracleDebugger?.detach({ sessionKey });
          } else {
            await window.electronAPI?.oracleDebugger?.stop({ sessionKey });
          }
        } catch {
          try {
            await window.electronAPI?.oracleDebugger?.detach({ sessionKey });
          } catch { /* best-effort */ }
        }
      })();
    }
  }
}

/**
 * Fire-and-forget cleanup of result caches for tabs being removed.
 * In-flight CSV exports are terminated by source disposal — TempSqlite.getWindow
 * throws SourceDisposedError, which streamExportCsv detects and resolves as cancelled.
 */
function cleanupResultCaches(tabs: Tab[]) {
  for (const tab of tabs) {
    if (isQueryTab(tab)) {
      if (tab.content.resultSetCacheUris?.length) {
        for (const uri of tab.content.resultSetCacheUris) {
          if (!uri) continue;
          void window.electronAPI?.resultCache?.discard({ uri }).catch(() => {});
        }
      }
      for (const pinned of tab.content.pinnedResultSets ?? []) {
        for (const uri of pinned.sourceUris ?? []) {
          if (!uri) continue;
          void window.electronAPI?.resultCache?.discard({ uri }).catch(() => {});
        }
      }
    }
  }
}

/** Strip stale runtime debug state so restored tabs start fresh. */
function sanitizeTabForHistory(tab: Tab): Tab {
  if (isPlSqlDebuggerTab(tab)) {
    return {
      ...tab,
      content: {
        ...tab.content,
        sessionKey: undefined,
        debugState: 'idle' as const,
        currentLine: undefined,
        variables: [],
        callStack: [],
        displayedProgram: undefined,
      },
    };
  }
  if (isPlSqlEditorTab(tab)) {
    return {
      ...tab,
      content: {
        ...tab.content,
        sessionKey: undefined,
        debugState: 'idle' as const,
        currentLine: undefined,
        variables: [],
        callStack: [],
        displayedProgram: undefined,
        mode: 'edit' as const,
      },
    };
  }
  return tab;
}

interface WorkspaceState {
  // State
  tabs: Tab[];
  activeTabId: string | null;
  /**
   * Per tab id, bumped whenever that tab is restored (`restoreTabs`,
   * `addRestoredTab`): a restored tab may reuse the id of one that was open
   * before, and the query editor keys its reset of that id's run state on this
   * (Codex r7).
   */
  tabRestoreGenerations: Record<string, number>;

  // Actions
  addTab: (tab: NewTabInput) => string;
  removeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  getNextQueryNumber: () => number;
  getNextNotebookNumber: () => number;
  updateTabContent: <T extends Tab['type']>(
    tabId: string,
    content: Partial<Extract<Tab, { type: T }>['content']>
  ) => void;
  updateTabConnectionInfo: (tabId: string, connectionInfo: Partial<TabConnectionInfo>) => void;
  /** Set or clear the Pinned Workspace link on a single tab (undefined severs the link). */
  setSourcePin: (tabId: string, sourcePin: TabSourcePin | undefined) => void;
  /**
   * Clear the `sourcePin` link on every tab whose `sourcePin` satisfies `predicate`.
   * Used when a pin is deleted (F11) or a tab is rebound to another connection (F2).
   */
  clearSourcePins: (predicate: (sourcePin: TabSourcePin) => boolean) => void;
  getTab: (tabId: string) => Tab | undefined;
  /** Update tab title. If isCustom is true, prevents auto-updates. */
  updateTabTitle: (tabId: string, title: string, isCustom?: boolean) => void;
  /** Reset tab title to auto-generated. Sets customTitle to false. */
  resetTabTitle: (tabId: string, autoTitle: string) => void;
  /** Get all tab titles for deduplication */
  getTabTitles: () => string[];
  /** Batch update multiple tab titles (for renumbering duplicates) */
  batchUpdateTabTitles: (titleMap: Map<string, string>) => void;
  /** Restore tabs from session (replaces all existing tabs) */
  restoreTabs: (tabs: Tab[], activeTabId: string | null) => void;
  /**
   * Append a single already-built tab (id preserved) to the current workspace
   * and activate it, without touching the other tabs. Used to restore one
   * deserialized tab (e.g. from a pinned workspace) into the live workspace.
   */
  addRestoredTab: (tab: Tab) => void;
  /** Pin a tab (moves it to the left, protects from bulk close) */
  pinTab: (tabId: string) => void;
  /** Unpin a tab */
  unpinTab: (tabId: string) => void;
  /** Close all tabs except the specified tab (respects pinned tabs) */
  closeOtherTabs: (tabId: string, keep?: ReadonlySet<string>) => void;
  /** Close all tabs to the right of the specified tab (respects pinned tabs) */
  closeTabsToRight: (tabId: string, keep?: ReadonlySet<string>) => void;
  /** Close all unpinned tabs */
  /** `keep`: tabs to leave open — ones whose transaction could not be ended (`requestTabsClose`). */
  closeUnpinnedTabs: (keep?: ReadonlySet<string>) => void;
  /** Reorder tabs by moving a tab from one index to another */
  reorderTabs: (fromIndex: number, toIndex: number) => void;
  /** Move a tab left or right within its group (pinned/unpinned) */
  moveTab: (tabId: string, direction: 'left' | 'right') => void;
  /** Ensure a StartPage tab exists and is active. Creates one if none exists (singleton behavior). */
  ensureStartPage: () => string;
  /** Get the StartPage tab ID if one exists */
  getStartPageTabId: () => string | null;
  /**
   * Remove all `operator: 'json'` filter conditions targeting the given
   * (connection, database, schema, table, column) tuple from open
   * table-explorer tabs. Returns the number of conditions removed.
   * FilterGroups that become empty after removal are dropped entirely.
   */
  removeJsonOrphanFilters: (target: {
    connectionId: string;
    database: string;
    schema: string;
    table: string;
    column: string;
  }) => number;
  /**
   * Remove all `operator: 'json'` filter conditions whose JSONPath matches
   * `target.path` AND whose sub-operator is one of the enum sub-operators
   * (`property_enum` / `any_enum` / `all_enum`) targeting the given
   * (connection, database, schema, table, column) tuple from open
   * table-explorer tabs. Returns the number of conditions removed.
   * FilterGroups that become empty after removal are dropped entirely.
   * Narrower than `removeJsonOrphanFilters`: only enum-sub-operator
   * conditions on the matching path are removed.
   */
  removeEnumPathOrphanFilters: (target: {
    connectionId: string;
    database: string;
    schema: string;
    table: string;
    column: string;
    path: string;
  }) => number;
}

/**
 * Creates default content based on workspace type.
 * For query tabs, initializes editorOptions from the global defaults in appStore.
 *
 * `input` is the caller's partial content (if any). W5.2 fix round 1 (F-3)
 * uses it to derive the engine for schema-compare tabs — see that case.
 */
function createDefaultContent(type: WorkspaceType, input?: unknown): Tab['content'] {
  switch (type) {
    case 'query': {
      // Get current global editor settings to use as defaults for this tab
      const { editorSettings } = useAppStore.getState();
      return {
        query: '',
        resultSets: [],
        messages: [],
        results: [],
        columns: [],
        editorOptions: {
          wordWrap: editorSettings.wordWrap,
          formatOnExecute: editorSettings.formatOnExecute,
        },
        transactionMode: 'auto',
        transactionState: 'idle',
        isolationLevel: 'default',
      };
    }
    case 'table-editor':
      return {
        tableName: '',
        schema: 'dbo',
        filters: [],
        pendingChanges: [],
        rows: [],
        columns: [],
      };
    case 'table-explorer':
      return {
        tableName: '',
        schema: 'dbo',
        database: '',
        rows: [],
        columns: [],
        limit: 100,
        selectedColumns: '*',
        filters: [],
        sorting: [],
        isLoading: false,
        isEditMode: false,
        pendingChanges: [],
        primaryKeyColumns: [],
      };
    case 'plan':
      return {
        view: 'single',
      };
    case 'table-designer':
      return {
        mode: 'create',
        tableName: '',
        schema: 'dbo',
        database: '',
        columns: [],
        isDirty: false,
        isLoading: false,
        isApplying: false,
      };
    case 'security':
      return createDefaultSecurityManagerContent();
    case 'schema-compare': {
      // W5.2 fix round 1 (F-3): navigation-created tabs ("Compare schema
      // with…" in the Object Explorer, "Apply schema to DB" from a Blueprint)
      // land here with an endpoint already picked but no options — and the
      // zero-arg factory used to bake a cross-engine ignore list that
      // `inferDepth` then reported as 'custom'. Derive the engine from
      // whichever endpoint the caller supplied so the baseline carries THAT
      // engine's Standard preset.
      const endpoints = (input ?? {}) as {
        source?: SchemaCompareEndpoint;
        target?: SchemaCompareEndpoint;
      };
      const engine =
        resolveEngineForConnectionId(endpoints.source?.connectionId) ??
        resolveEngineForConnectionId(endpoints.target?.connectionId);
      return createDefaultSchemaCompareContent(undefined, undefined, engine);
    }
    case 'data-compare':
      return createDefaultDataCompareContent();
    case 'dependency-viewer':
      return createDefaultDependencyViewerContent('', 'table', '', '');
    case 'schema-overview':
      return createDefaultSchemaOverviewContent('', '');
    case 'spatial-explorer':
      return createDefaultSpatialExplorerContent('', '');
    case 'data-profile':
      return createDefaultDataProfileContent({
        connectionId: '',
        database: '',
        schema: '',
        table: '',
      });
    case 'start-page':
      return createDefaultStartPageContent();
    case 'notebook':
      return createDefaultNotebookContent();
    case 'session-browser':
      return createDefaultSessionBrowserContent();
    case 'tablespace-manager':
      return createDefaultTablespaceManagerContent();
    case 'oracle-object-detail':
      return createDefaultOracleObjectDetailContent('', '', '', 'sequence');
    case 'data-pump':
      return createDefaultDataPumpContent();
    case 'instance-monitor':
      return createDefaultInstanceMonitorContent();
    case 'filegroup-manager':
      return createDefaultFilegroupManagerContent();
    case 'pl-sql-debugger':
      return createDefaultPlSqlDebuggerContent('', '', '');
    case 'job-scheduler':
      return createDefaultJobSchedulerContent();
    case 'plsql-editor':
      return createDefaultPlSqlEditorContent('', '', '');
    case 'package-explorer':
      return createDefaultPackageExplorerContent({ connectionId: '', database: '' });
    case 'references-tab':
      return createDefaultReferencesContent();
    case 'database-blueprint':
      return createDefaultDatabaseBlueprintContent('');
    case 'cross-engine-migrate':
      return createDefaultCrossEngineMigrateContent();
    case 'dashboard':
      return createDefaultDashboardContent();
    case 'data-import':
      return createDefaultDataImportContent();
    case 'deadlock-analyzer':
      return createDefaultDeadlockAnalyzerContent();
    case 'query-store':
      return createDefaultQueryStoreContent();
    case 'storage-explorer':
      return createDefaultStorageExplorerContent();
    default: {
      // Exhaustive check - this should never happen
      const _exhaustive: never = type;
      throw new Error(`Unknown workspace type: ${_exhaustive}`);
    }
  }
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  // Initial state
  tabs: [createStartPageTab()],
  activeTabId: DEFAULT_START_PAGE_TAB_ID,
  tabRestoreGenerations: {},

  // Actions
  addTab: (tab) => {
    addBreadcrumb('navigation', `tab-create:${tab.type}`);
    // Track pageview for the new workspace type. `pageviewForTab` is the ONLY
    // producer of these arguments and it takes no title: every type answers a
    // static surface label, so no tab title (container path, `schema.table`,
    // file or query name) can reach the outbound payload (spec §6.1, P1 (a)).
    if (typeof window !== 'undefined' && window.electronAPI?.analytics?.pageview) {
      const pageview = pageviewForTab(tab.type);
      window.electronAPI.analytics.pageview(pageview.url, pageview.title).catch(() => {});
    }
    // Use timestamp + random for unique IDs even when called rapidly
    const id = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const defaultContent = createDefaultContent(tab.type, tab.content);
    const mergedContent = tab.content
      ? { ...defaultContent, ...tab.content }
      : defaultContent;

    set((state) => ({
      tabs: [
        {
          ...tab,
          // Security slice: a title / connection name / host never carries raw secret-bearing target text.
          title: safeTabTitle(tab.title),
          ...(tab.connectionInfo ? { connectionInfo: safeTabConnectionInfo(tab.connectionInfo) } : {}),
          id,
          content: mergedContent,
        } as Tab,
        ...state.tabs,
      ],
      activeTabId: id, // Activate the new tab
    }));
    return id;
  },

  removeTab: (tabId) => {
    // Get the tab before removing to save to history
    const tabToRemove = get().tabs.find((tab) => tab.id === tabId);
    if (tabToRemove?.type === 'start-page') {
      return;
    }
    if (tabToRemove) {
      addBreadcrumb('navigation', `tab-close:${tabToRemove.type}`);
      // Release backend debug session before saving to history
      cleanupDebuggerTabs([tabToRemove]);
      // Free result cache memory in Electron main process
      cleanupResultCaches([tabToRemove]);
      // Drop the in-memory deadlock report (up to 10 MiB of XML) held for this tab
      if (tabToRemove.type === 'deadlock-analyzer') releaseDeadlockAnalyzerTab(tabToRemove.id);
      cleanupStorageExplorerTabs([tabToRemove]);
      // Save to tab history (strip stale debug state so restored tabs start fresh)
      useTabHistoryStore.getState().addClosedTab(sanitizeTabForHistory(tabToRemove));
      // Show retention toast for file-backed query tabs
      showRetentionToast(tabToRemove);
    }

    set((state) => {
      const tabIndex = state.tabs.findIndex((tab) => tab.id === tabId);
      if (tabIndex === -1) return state;

      let newTabs = [...state.tabs];
      newTabs.splice(tabIndex, 1);

      // Determine the new active tab if the removed tab was active
      let newActiveTabId = state.activeTabId;
      if (state.activeTabId === tabId) {
        // If there are tabs left, activate the tab to the left, or the first tab
        if (newTabs.length > 0) {
          const newActiveIndex = Math.min(tabIndex, newTabs.length - 1);
          newActiveTabId = newTabs[newActiveIndex].id;
        } else {
          newActiveTabId = null;
        }
      }

      // Clean up orphaned prefixes (tabs that are now alone but still have a prefix)
      const cleanupMap = cleanupOrphanedPrefixes(newTabs);
      if (cleanupMap) {
        newTabs = newTabs.map((tab) => {
          const newTitle = cleanupMap.get(tab.id);
          if (newTitle) {
            return { ...tab, title: newTitle } as Tab;
          }
          return tab;
        });
      }

      return { tabs: newTabs, activeTabId: newActiveTabId };
    });
  },

  setActiveTab: (tabId) => {
    const tab = get().tabs.find((t) => t.id === tabId);
    if (tab) {
      addBreadcrumb('tab_switch', `tab:${tab.type}`);
    }
    set({ activeTabId: tabId });
  },

  updateTabContent: (tabId, content) => {
    set((state) => {
      const tabIndex = state.tabs.findIndex((tab) => tab.id === tabId);
      if (tabIndex === -1) return state;

      const newTabs = [...state.tabs];
      const tab = newTabs[tabIndex];

      const mergedContent = {
        ...tab.content,
        ...content,
      };

      // G4 — Save As severs a file-linked sourcePin. We deliberately do NOT
      // try to determine "is the linked pin a file pin" from inside the store
      // — that's the pinned-objects domain, and reaching into it here would be
      // wrong layering. Instead we sever on a narrower, always-safe signal:
      // the tab's `filePath` was already NON-EMPTY (it already pointed at a
      // saved file) and is now changing to a different value. A snapshot-linked
      // pin's tab has an EMPTY previous `filePath` on its first Save (nothing
      // to diverge from yet), so that case is excluded and the link survives;
      // only a SUBSEQUENT Save As (previous filePath present, now changing)
      // severs it — a Save As tab no longer represents any pinned definition's
      // origin, per spec. This only ever matches query/notebook tabs in
      // practice since only those carry both `filePath` and `sourcePin`.
      let sourcePin = tab.sourcePin;
      if (sourcePin && (isQueryTab(tab) || isNotebookTab(tab))) {
        const prevFilePath = tab.content.filePath;
        const nextFilePath = (mergedContent as { filePath?: string }).filePath;
        if (prevFilePath && nextFilePath && nextFilePath !== prevFilePath) {
          sourcePin = undefined;
        }
      }

      newTabs[tabIndex] = {
        ...tab,
        content: mergedContent,
        sourcePin,
      } as Tab;

      return { tabs: newTabs };
    });
  },

  updateTabConnectionInfo: (tabId, connectionInfo) => {
    set((state) => {
      const tabIndex = state.tabs.findIndex((tab) => tab.id === tabId);
      if (tabIndex === -1) return state;

      const newTabs = [...state.tabs];
      const tab = newTabs[tabIndex];

      // F2 — a sourcePin is scoped to the connection it was pinned FROM. Only
      // an actual REBIND to a different connectionId severs it; changing just
      // the database (or any other connectionInfo field) while the
      // connectionId stays the same is divergence, not severing, and must
      // leave the link intact — the pin button surfaces that case instead.
      let sourcePin = tab.sourcePin;
      if (
        sourcePin &&
        connectionInfo.connectionId !== undefined &&
        connectionInfo.connectionId !== tab.connectionInfo?.connectionId
      ) {
        sourcePin = undefined;
      }

      newTabs[tabIndex] = {
        ...tab,
        connectionInfo: {
          ...tab.connectionInfo,
          ...safeTabConnectionInfo(connectionInfo),
        },
        sourcePin,
      } as Tab;

      return { tabs: newTabs };
    });
  },

  setSourcePin: (tabId, sourcePin) => {
    set((state) => {
      const tabIndex = state.tabs.findIndex((tab) => tab.id === tabId);
      if (tabIndex === -1) return state;

      const newTabs = [...state.tabs];
      const tab = newTabs[tabIndex];

      newTabs[tabIndex] = {
        ...tab,
        sourcePin,
      } as Tab;

      return { tabs: newTabs };
    });
  },

  clearSourcePins: (predicate) => {
    set((state) => {
      let changed = false;
      const newTabs = state.tabs.map((tab) => {
        if (!tab.sourcePin || !predicate(tab.sourcePin)) return tab;
        changed = true;
        return { ...tab, sourcePin: undefined } as Tab;
      });

      if (!changed) return state;
      return { tabs: newTabs };
    });
  },

  getNextQueryNumber: () => {
    const { tabs } = get();
    const queryTabs = tabs.filter((tab) => tab.title.startsWith('Query-'));

    // Extract existing query numbers
    const queryNumbers = queryTabs
      .map((tab) => {
        const match = tab.title.match(/^Query-(\d+)$/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter((num) => !isNaN(num));

    // Find the next available number
    if (queryNumbers.length === 0) return 1;

    // Sort numbers and find the first gap or return max + 1
    queryNumbers.sort((a, b) => a - b);

    for (let i = 0; i < queryNumbers.length; i++) {
      if (queryNumbers[i] !== i + 1) {
        return i + 1;
      }
    }

    return queryNumbers.length + 1;
  },

  getNextNotebookNumber: () => {
    const { tabs } = get();
    const notebookTabs = tabs.filter((tab) => tab.title.startsWith('Notebook-'));
    const numbers = notebookTabs
      .map((tab) => {
        const match = tab.title.match(/^Notebook-(\d+)$/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter((num) => !isNaN(num));

    if (numbers.length === 0) return 1;
    numbers.sort((a, b) => a - b);
    for (let i = 0; i < numbers.length; i++) {
      if (numbers[i] !== i + 1) return i + 1;
    }
    return numbers.length + 1;
  },

  getTab: (tabId) => {
    return get().tabs.find((tab) => tab.id === tabId);
  },

  updateTabTitle: (tabId, title, isCustom = false) => {
    set((state) => {
      const tabIndex = state.tabs.findIndex((tab) => tab.id === tabId);
      if (tabIndex === -1) return state;

      const newTabs = [...state.tabs];
      const tab = newTabs[tabIndex];

      newTabs[tabIndex] = {
        ...tab,
        title: safeTabTitle(title),
        customTitle: isCustom,
      } as Tab;

      return { tabs: newTabs };
    });
  },

  resetTabTitle: (tabId, autoTitle) => {
    set((state) => {
      const tabIndex = state.tabs.findIndex((tab) => tab.id === tabId);
      if (tabIndex === -1) return state;

      const newTabs = [...state.tabs];
      const tab = newTabs[tabIndex];

      newTabs[tabIndex] = {
        ...tab,
        title: safeTabTitle(autoTitle),
        customTitle: false,
      } as Tab;

      return { tabs: newTabs };
    });
  },

  getTabTitles: () => {
    return get().tabs.map((tab) => tab.title);
  },

  batchUpdateTabTitles: (titleMap) => {
    if (titleMap.size === 0) return;

    set((state) => {
      const newTabs = state.tabs.map((tab) => {
        const newTitle = safeTabTitle(titleMap.get(tab.id));
        if (newTitle && newTitle !== tab.title) {
          return { ...tab, title: newTitle } as Tab;
        }
        return tab;
      });
      return { tabs: newTabs };
    });
  },

  restoreTabs: (tabs, activeTabId) => {
    const outgoingTabs = get().tabs;
    cleanupDebuggerTabs(outgoingTabs);
    cleanupResultCaches(outgoingTabs);
    cleanupStorageExplorerTabs(outgoingTabs);

    // Restored tabs come from session files an older build may have written with raw target text (security slice).
    const safeTabs = tabs.map(safeTab);
    const hasStartPage = safeTabs.some((tab) => tab.type === 'start-page');
    const tabsWithStartPage = hasStartPage ? safeTabs : [...safeTabs, createStartPageTab()];

    set((state) => ({
      tabs: tabsWithStartPage,
      activeTabId:
        activeTabId ||
        (tabsWithStartPage.length > 0 ? tabsWithStartPage[0].id : null),
      tabRestoreGenerations: Object.fromEntries(
        tabs.map((tab) => [tab.id, (state.tabRestoreGenerations[tab.id] ?? 0) + 1]),
      ),
    }));
  },

  addRestoredTab: (tab) => {
    set((state) => ({
      tabs: sortTabsByPinned([...state.tabs, safeTab(tab)]),
      activeTabId: tab.id,
      tabRestoreGenerations: {
        ...state.tabRestoreGenerations,
        [tab.id]: (state.tabRestoreGenerations[tab.id] ?? 0) + 1,
      },
    }));
  },

  pinTab: (tabId) => {
    set((state) => {
      const tabIndex = state.tabs.findIndex((tab) => tab.id === tabId);
      if (tabIndex === -1) return state;

      const newTabs = [...state.tabs];
      const tab = newTabs[tabIndex];
      if (tab.type === 'start-page') return state;

      // Update the tab's isPinned flag
      newTabs[tabIndex] = { ...tab, isPinned: true } as Tab;

      // Sort tabs: pinned tabs first (preserving order within each group)
      return { tabs: sortTabsByPinned(newTabs) };
    });
  },

  unpinTab: (tabId) => {
    set((state) => {
      const tabIndex = state.tabs.findIndex((tab) => tab.id === tabId);
      if (tabIndex === -1) return state;

      const newTabs = [...state.tabs];
      const tab = newTabs[tabIndex];
      if (tab.type === 'start-page') return state;

      // Update the tab's isPinned flag
      newTabs[tabIndex] = { ...tab, isPinned: false } as Tab;

      // Sort tabs: pinned tabs first (preserving order within each group)
      return { tabs: sortTabsByPinned(newTabs) };
    });
  },

  closeOtherTabs: (tabId, keep) => {
    const tabsToClose = get().tabs.filter(
      (tab) => tab.id !== tabId && !tab.isPinned && tab.type !== 'start-page' && !keep?.has(tab.id)
    );

    // Release backend debug sessions before closing
    cleanupDebuggerTabs(tabsToClose);
    // Free result cache memory in Electron main process
    cleanupResultCaches(tabsToClose);
    cleanupStorageExplorerTabs(tabsToClose);
    // Save tabs to history (sanitize stale debug state)
    for (const tab of tabsToClose) {
      useTabHistoryStore.getState().addClosedTab(sanitizeTabForHistory(tab));
    }

    set((state) => {
      // Keep only the target tab and all pinned tabs
      const newTabs = state.tabs.filter(
        (tab) => tab.id === tabId || tab.isPinned || tab.type === 'start-page' || keep?.has(tab.id)
      );

      // Switch active tab to the target tab
      return { tabs: newTabs, activeTabId: tabId };
    });
  },

  closeTabsToRight: (tabId, keep) => {
    const { tabs } = get();
    const tabIndex = tabs.findIndex((tab) => tab.id === tabId);
    if (tabIndex === -1) return;

    // Find tabs to the right that are not pinned
    const tabsToClose = tabs
      .slice(tabIndex + 1)
      .filter((tab) => !tab.isPinned && tab.type !== 'start-page' && !keep?.has(tab.id));

    // Release backend debug sessions before closing
    cleanupDebuggerTabs(tabsToClose);
    // Free result cache memory in Electron main process
    cleanupResultCaches(tabsToClose);
    cleanupStorageExplorerTabs(tabsToClose);
    // Save tabs to history (sanitize stale debug state)
    for (const tab of tabsToClose) {
      useTabHistoryStore.getState().addClosedTab(sanitizeTabForHistory(tab));
    }

    set((state) => {
      const currentIndex = state.tabs.findIndex((t) => t.id === tabId);
      if (currentIndex === -1) return state;

      // Keep tabs at or before target index, plus any pinned tabs after
      const newTabs = state.tabs.filter((tab, idx) => {
        // Keep tabs at or before the target
        if (idx <= currentIndex) return true;
        if (tab.type === 'start-page' || keep?.has(tab.id)) return true;
        // Keep pinned tabs after the target
        return tab.isPinned;
      });

      // If active tab was closed, switch to the target tab
      const activeWasRemoved = !newTabs.find((t) => t.id === state.activeTabId);
      const newActiveTabId = activeWasRemoved ? tabId : state.activeTabId;

      return { tabs: newTabs, activeTabId: newActiveTabId };
    });
  },

  closeUnpinnedTabs: (keep) => {
    const { tabs } = get();
    const unpinnedTabs = tabs.filter((tab) => !tab.isPinned && tab.type !== 'start-page' && !keep?.has(tab.id));

    // Release backend debug sessions before closing
    cleanupDebuggerTabs(unpinnedTabs);
    // Free result cache memory in Electron main process
    cleanupResultCaches(unpinnedTabs);
    cleanupStorageExplorerTabs(unpinnedTabs);
    // Save unpinned tabs to history (sanitize stale debug state)
    for (const tab of unpinnedTabs) {
      useTabHistoryStore.getState().addClosedTab(sanitizeTabForHistory(tab));
    }

    set((state) => {
      // Keep only pinned tabs and the StartPage
      const newTabs = state.tabs.filter(
        (tab) => tab.isPinned || tab.type === 'start-page' || keep?.has(tab.id)
      );

      // Determine new active tab
      let newActiveTabId: string | null = null;
      const currentActiveTab = state.tabs.find((t) => t.id === state.activeTabId);

      if (currentActiveTab?.isPinned || currentActiveTab?.type === 'start-page' || keep?.has(currentActiveTab?.id ?? '')) {
        // Active tab is pinned or StartPage, keep it active
        newActiveTabId = state.activeTabId;
      } else if (newTabs.length > 0) {
        // Active tab was unpinned and closed, switch to StartPage (fallback)
        const startPageTab = newTabs.find((tab) => tab.type === 'start-page');
        newActiveTabId = startPageTab?.id ?? newTabs[newTabs.length - 1].id;
      }
      // If no pinned tabs or StartPage remain, activeTabId will be null

      return { tabs: newTabs, activeTabId: newActiveTabId };
    });
  },

  reorderTabs: (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    set((state) => {
      const { tabs } = state;
      if (fromIndex < 0 || fromIndex >= tabs.length) return state;
      if (toIndex < 0 || toIndex >= tabs.length) return state;

      const newTabs = [...tabs];
      const [movedTab] = newTabs.splice(fromIndex, 1);
      newTabs.splice(toIndex, 0, movedTab);

      return { tabs: newTabs };
    });
  },

  moveTab: (tabId, direction) => {
    set((state) => {
      const { tabs } = state;
      const currentIndex = tabs.findIndex((t) => t.id === tabId);
      if (currentIndex === -1) return state;

      const tab = tabs[currentIndex];
      if (tab.type === 'start-page') return state;
      const isPinned = tab.isPinned;

      // Find the boundaries for this tab's group
      const firstUnpinnedIndex = tabs.findIndex((t) => !t.isPinned);
      const groupStartIndex = isPinned ? 0 : firstUnpinnedIndex;
      const groupEndIndex = isPinned
        ? (firstUnpinnedIndex === -1 ? tabs.length - 1 : firstUnpinnedIndex - 1)
        : tabs.length - 1;

      // Calculate new index within group boundaries
      let newIndex: number;
      if (direction === 'left') {
        newIndex = currentIndex - 1;
        if (newIndex < groupStartIndex) return state; // Can't move further left
      } else {
        newIndex = currentIndex + 1;
        if (newIndex > groupEndIndex) return state; // Can't move further right
      }

      // Perform the swap
      const newTabs = [...tabs];
      const [movedTab] = newTabs.splice(currentIndex, 1);
      newTabs.splice(newIndex, 0, movedTab);

      return { tabs: newTabs };
    });
  },

  getStartPageTabId: () => {
    const { tabs } = get();
    const startPageTab = tabs.find((tab) => tab.type === 'start-page');
    return startPageTab?.id ?? null;
  },

  ensureStartPage: () => {
    const { activeTabId, getStartPageTabId, addTab, setActiveTab } = get();

    // Check if a StartPage tab already exists
    const existingStartPageId = getStartPageTabId();
    if (existingStartPageId) {
      // Activate it if not already active
      if (activeTabId !== existingStartPageId) {
        setActiveTab(existingStartPageId);
      }
      return existingStartPageId;
    }

    // Create a new StartPage tab (singleton)
    const newTabId = addTab({
      title: 'Home',
      type: 'start-page',
    });

    return newTabId;
  },

  removeJsonOrphanFilters: ({ connectionId, database, schema, table, column }) => {
    let removed = 0;
    set((state) => {
      const newTabs = state.tabs.map((tab) => {
        if (tab.type !== 'table-explorer') return tab;
        if (tab.connectionInfo?.connectionId !== connectionId) return tab;
        if (tab.content.database !== database) return tab;
        if (tab.content.schema !== schema) return tab;
        if (tab.content.tableName !== table) return tab;

        let tabRemoved = 0;
        const newGroups = tab.content.filters
          .map((group) => {
            if (group.field !== column) return group;
            const kept = group.conditions.filter((c) => c.operator !== 'json');
            tabRemoved += group.conditions.length - kept.length;
            return { ...group, conditions: kept };
          })
          .filter((group) => group.conditions.length > 0);

        if (tabRemoved === 0) return tab;
        removed += tabRemoved;
        return {
          ...tab,
          content: { ...tab.content, filters: newGroups },
        } as Tab;
      });

      if (removed === 0) return state;
      return { tabs: newTabs };
    });
    return removed;
  },

  removeEnumPathOrphanFilters: ({ connectionId, database, schema, table, column, path }) => {
    let removed = 0;
    set((state) => {
      const newTabs = state.tabs.map((tab) => {
        if (tab.type !== 'table-explorer') return tab;
        if (tab.connectionInfo?.connectionId !== connectionId) return tab;
        if (tab.content.database !== database) return tab;
        if (tab.content.schema !== schema) return tab;
        if (tab.content.tableName !== table) return tab;

        let tabRemoved = 0;
        const newGroups = tab.content.filters
          .map((group) => {
            if (group.field !== column) return group;
            const kept = group.conditions.filter(
              (c) =>
                !(
                  c.operator === 'json' &&
                  c.path === path &&
                  (c.jsonSubOperator === 'property_enum' ||
                    c.jsonSubOperator === 'any_enum' ||
                    c.jsonSubOperator === 'all_enum')
                ),
            );
            tabRemoved += group.conditions.length - kept.length;
            return { ...group, conditions: kept };
          })
          .filter((group) => group.conditions.length > 0);

        if (tabRemoved === 0) return tab;
        removed += tabRemoved;
        return {
          ...tab,
          content: { ...tab.content, filters: newGroups },
        } as Tab;
      });

      if (removed === 0) return state;
      return { tabs: newTabs };
    });
    return removed;
  },
}));

/**
 * Sort tabs so pinned tabs appear first, preserving relative order within each group.
 * Exported for unit testing.
 */
export function sortTabsByPinned(tabs: Tab[]): Tab[] {
  const pinned = tabs.filter((t) => t.isPinned);
  const unpinned = tabs.filter((t) => !t.isPinned);
  return [...pinned, ...unpinned];
}
